import { createContext, useContext } from 'react'

/**
 * Whether the report currently on screen has produced a result — spec 092.
 *
 * The run control has two states: the orange "Run report" CTA before there is
 * anything on screen, and a neutral "Refresh" once there is. On the generic engine
 * ReportView knows that directly. On the specialized path it does NOT: each of the
 * 20 pages builds its own <SpecializedFilterBar> and hands it to the shell as a
 * prop, so the bar is rendered by the shell but constructed by the page — and the
 * page is the one thing that does not track the shell's result state.
 *
 * Rather than add a prop to 20 call sites, the shell publishes the signal and the
 * bar reads it. Same trick, and the same reason, as ColumnAliasProvider and
 * ReportExportViewContext: the shell is the one place all 21 specialized reports
 * pass through.
 *
 * A page may still override by passing `hasRun` explicitly — an explicit prop
 * beats the ambient value, so a page that knows better is never fought by this.
 */
export const ReportRunStateContext = createContext(null)

export const useReportHasRun = () => useContext(ReportRunStateContext)?.hasRun ?? false

/**
 * Host-owned buttons for the GRID's result strip, to the left of Display View.
 *
 * "Rename columns" is the case this exists for. It used to sit in a floating row
 * above the result on both hosts, and it belongs on the strip beside the other
 * column controls. But the button cannot simply be passed down as a prop: on the
 * generic engine ReportView renders the grid itself and could, while
 * SpecializedReportShell renders the grid as `{children}` — the PAGE constructs
 * it — so the shell has no way to hand a prop to the grid inside it. That is the
 * same shape of problem `hasRun` above solves, and it gets the same answer: the
 * host publishes, the grid reads.
 *
 * The host keeps ownership of the modal and of the permission check
 * (`canManage` is resolved server-side against CB's own permission, never
 * inferred client-side), so this carries only already-rendered JSX. A grid that
 * finds nothing here renders nothing — the 9 non-report pages sharing DataGrid
 * are untouched, since they have no provider above them.
 */
export const useReportStripActions = () => useContext(ReportRunStateContext)?.stripActions ?? null

/**
 * The report's own name, published by the host.
 *
 * CsvReportGrid needs it to recognise when CB's CSV preamble is just restating
 * the report title, which it renders inches below the page h1. Threading a prop
 * would mean editing all seven pages that mount that grid; publishing it here
 * costs one line in the shell and reaches every one of them.
 */
export const useReportName = () => useContext(ReportRunStateContext)?.reportName ?? ''

/**
 * Spec 131 — the ticket of the run most recently STARTED on this page
 * (`useLatestRun().runKey`, published by SpecializedReportShell). `undefined`
 * outside a specialized report.
 *
 * Why a key and not the running edge: a run that supersedes one still in flight
 * keeps `running` true throughout, so "a run started" is no longer visible as a
 * false→true transition. SpecializedFilterBar clears its stale-since-reset band
 * on this instead. Carried here rather than as a prop on each page's filter bar,
 * so no page can be missed (the same reason `hasRun` rides this context).
 */
export const useReportRunKey = () => useContext(ReportRunStateContext)?.runKey
